/**
 * Visual Inventory Mapping (VIM) Dashboard
 * 
 * UNIFIED INTERFACE - Integrates with existing systems:
 * - ad_inventory_pricing collection (via /api/marketing/ad-inventory-pricing)
 * - marketing_campaigns collection (via /api/marketing/campaigns)
 * - CDP for user personalization
 * - Multi-channel support (Shopify, Own Website, Mobile App)
 * 
 * This dashboard provides a unified view of:
 * 1. Ad inventory slots (from existing ad_inventory_pricing)
 * 2. Active campaigns (from existing marketing_campaigns)
 * 3. Channel configurations
 * 4. Performance stats
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutGrid, Plus, Edit2, Trash2, Settings, Monitor, Smartphone, Globe,
  DollarSign, Eye, MousePointer, TrendingUp, Video, Image, Layers,
  CheckCircle, XCircle, ChevronDown, ChevronRight, RefreshCw, Copy,
  AlertTriangle, Key, Shield, ExternalLink, ArrowRight
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Channel types
const CHANNEL_TYPES = {
  shopify: { label: 'Shopify Storefront', icon: Globe, color: 'bg-green-100 text-green-800' },
  own_website: { label: 'Own Website', icon: Monitor, color: 'bg-blue-100 text-blue-800' },
  mobile_app: { label: 'Mobile App', icon: Smartphone, color: 'bg-purple-100 text-purple-800' }
};

// Slot types from existing system
const SLOT_TYPES = {
  sponsored_listings: { label: 'Sponsored Listings', color: 'bg-amber-100 text-amber-800' },
  banner_ads: { label: 'Banner Ads', color: 'bg-blue-100 text-blue-800' },
  featured_placement: { label: 'Featured Placement', color: 'bg-purple-100 text-purple-800' },
  video_carousel: { label: 'Video Carousel', color: 'bg-pink-100 text-pink-800' }
};

// Pricing models
const PRICING_MODELS = {
  CPM: { label: 'CPM (per 1000 impressions)', color: 'bg-green-100 text-green-800' },
  CPC: { label: 'CPC (per click)', color: 'bg-blue-100 text-blue-800' }
};

// Ad types
const AD_TYPES = {
  banner: { label: 'Banner', icon: Image },
  video: { label: 'Video', icon: Video },
  carousel: { label: 'Carousel', icon: LayoutGrid },
  native: { label: 'Native', icon: Layers }
};

// Classifications
const CLASSIFICATIONS = {
  paid_ad: { label: 'Paid Ad', color: 'bg-green-100 text-green-800' },
  static: { label: 'Static Content', color: 'bg-gray-100 text-gray-800' },
  dynamic: { label: 'Dynamic Content', color: 'bg-blue-100 text-blue-800' }
};

// Default slot templates
const SLOT_TEMPLATES = [
  { id: 'homepage_hero', name: 'Homepage Hero Banner', placement: 'homepage', dimensions: { width: 1200, height: 400 }, ad_type: 'banner', cpm: 15 },
  { id: 'homepage_video', name: 'Homepage Video Carousel', placement: 'video_carousel', dimensions: { width: 400, height: 500 }, ad_type: 'video', cpm: 25 },
  { id: 'category_top', name: 'Category Page Top Banner', placement: 'category', dimensions: { width: 970, height: 250 }, ad_type: 'banner', cpm: 12 },
  { id: 'search_results', name: 'Search Results Sidebar', placement: 'search', dimensions: { width: 300, height: 250 }, ad_type: 'banner', cpm: 8 },
  { id: 'product_detail', name: 'Product Detail Recommendations', placement: 'product_detail', dimensions: { width: 300, height: 600 }, ad_type: 'native', cpm: 10 }
];


const VIMDashboard = () => {
  const { toast } = useToast();
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('inventory');
  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [channels, setChannels] = useState([]);
  
  // Form state
  const [showSlotForm, setShowSlotForm] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [slotForm, setSlotForm] = useState({
    slot_id: '',
    name: '',
    slot_type: 'sponsored_listings',
    pricing_model: 'CPM',
    cpm_rate: 100,
    cpc_rate: 10,
    min_budget: 80,
    max_products: 10,
    is_active: true,
    channel_id: 'shopify',
    ad_type: 'banner',
    dimensions: { width: 970, height: 250 },
    placement_location: 'homepage',
    position_index: 0,
    base_cpm: 10,
    min_bid: 1,
    placeholder_url: ''
  });
  
  // Channel form state
  const [showChannelForm, setShowChannelForm] = useState(false);
  const [channelForm, setChannelForm] = useState({
    channel_type: 'own_website',
    name: ''
  });
  const [newChannelCredentials, setNewChannelCredentials] = useState(null);

  // Fetch all data from EXISTING marketing APIs
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch from existing marketing endpoints + new recommend endpoints
      const [slotsRes, campaignsRes, statsRes, channelsRes] = await Promise.all([
        apiCall('/api/marketing/ad-inventory-pricing'),
        apiCall('/api/marketing/campaigns'),
        fetch(`${API_URL}/api/recommend/vim/stats`),
        fetch(`${API_URL}/api/recommend/channels`)
      ]);
      
      // Slots from existing ad_inventory_pricing
      if (Array.isArray(slotsRes)) {
        setSlots(slotsRes);
      }
      
      // Campaigns from existing marketing_campaigns
      if (campaignsRes?.campaigns || Array.isArray(campaignsRes)) {
        setCampaigns(campaignsRes?.campaigns || campaignsRes || []);
      }
      
      // Stats and channels from new endpoints
      const statsData = await statsRes.json();
      const channelsData = await channelsRes.json();
      
      if (statsData.success) setStats(statsData.stats);
      if (channelsData.success) setChannels(channelsData.channels || []);
    } catch (error) {
      console.error('Error fetching VIM data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load inventory data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast, apiCall]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Create/Update slot - uses EXISTING marketing API
  const handleSaveSlot = async () => {
    try {
      const slotData = {
        slot_id: slotForm.slot_id,
        name: slotForm.name,
        slot_type: slotForm.slot_type,
        pricing_model: slotForm.pricing_model,
        cpm_rate: slotForm.cpm_rate,
        cpc_rate: slotForm.cpc_rate,
        min_budget: slotForm.min_budget,
        max_products: slotForm.max_products,
        is_active: slotForm.is_active
      };
      
      // Use existing marketing API
      const url = editingSlot 
        ? `/api/marketing/ad-inventory-pricing/${slotForm.slot_id}`
        : '/api/marketing/ad-inventory-pricing';
      
      const result = await apiCall(url, {
        method: editingSlot ? 'PUT' : 'POST',
        body: JSON.stringify(slotData)
      });
      
      if (result) {
        toast({
          title: 'Success',
          description: editingSlot ? 'Slot updated successfully' : 'Slot created successfully'
        });
        setShowSlotForm(false);
        setEditingSlot(null);
        resetSlotForm();
        fetchData();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save slot',
        variant: 'destructive'
      });
    }
  };

  // Delete slot - uses EXISTING marketing API
  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm('Are you sure you want to delete this slot?')) return;
    
    try {
      await apiCall(`/api/marketing/ad-inventory-pricing/${slotId}`, {
        method: 'DELETE'
      });
      
      toast({ title: 'Success', description: 'Slot deleted' });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete slot',
        variant: 'destructive'
      });
    }
  };

  // Register channel
  const handleRegisterChannel = async () => {
    try {
      const response = await fetch(`${API_URL}/api/recommend/channels/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(channelForm)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setNewChannelCredentials(data);
        toast({
          title: 'Channel Registered',
          description: 'Save the API credentials securely'
        });
        fetchData();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to register channel',
        variant: 'destructive'
      });
    }
  };

  const resetSlotForm = () => {
    setSlotForm({
      slot_id: '',
      name: '',
      channel_id: 'shopify',
      slot_type: 'sponsored_listings',
      pricing_model: 'CPM',
      cpm_rate: 100,
      cpc_rate: 10,
      min_budget: 80,
      max_products: 10,
      is_active: true
    });
  };

  const handleEditSlot = (slot) => {
    setEditingSlot(slot);
    setSlotForm({
      slot_id: slot.slot_id,
      name: slot.name || slot.slot_id,
      slot_type: slot.slot_type || 'sponsored_listings',
      pricing_model: slot.pricing_model || 'CPM',
      cpm_rate: slot.cpm_rate || 100,
      cpc_rate: slot.cpc_rate || 10,
      min_budget: slot.min_budget || 80,
      max_products: slot.max_products || 10,
      is_active: slot.is_active !== false,
      channel_id: slot.channel_id || 'shopify'
    });
    setShowSlotForm(true);
  };

  const applyTemplate = (template) => {
    const slotId = `${template.id.toUpperCase()}_${Date.now().toString(36).toUpperCase()}`;
    setSlotForm({
      ...slotForm,
      slot_id: slotId,
      name: template.name,
      slot_type: template.ad_type === 'video' ? 'video_carousel' : 'sponsored_listings',
      cpm_rate: template.cpm * 10  // Convert to credits
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied!', description: 'Copied to clipboard' });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold">Visual Inventory Mapping (VIM)</h1>
            <p className="text-gray-500 mt-1 text-sm md:text-base">Define and manage ad inventory slots across channels</p>
          </div>
          <Button onClick={fetchData} variant="outline" size="sm" className="w-full sm:w-auto">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Slots</p>
                    <p className="text-2xl font-bold">{stats.total_slots}</p>
                  </div>
                  <LayoutGrid className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Paid Ad Slots</p>
                    <p className="text-2xl font-bold">{stats.by_classification?.paid_ad || 0}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Base CPM</p>
                    <p className="text-2xl font-bold">${stats.total_base_cpm?.toFixed(2)}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Channels</p>
                    <p className="text-2xl font-bold">{Object.keys(stats.by_channel || {}).length}</p>
                  </div>
                  <Globe className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="inventory">Ad Inventory</TabsTrigger>
            <TabsTrigger value="channels">Channels</TabsTrigger>
            <TabsTrigger value="campaigns">Active Campaigns</TabsTrigger>
          </TabsList>

          {/* Inventory Tab */}
          <TabsContent value="inventory" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Inventory Slots</h2>
              <Button onClick={() => { resetSlotForm(); setEditingSlot(null); setShowSlotForm(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Add Slot
              </Button>
            </div>

            {/* Slot Form Modal */}
            {showSlotForm && (
              <Card className="border-2 border-blue-200">
                <CardHeader>
                  <CardTitle>{editingSlot ? 'Edit Slot' : 'Create New Slot'}</CardTitle>
                  <CardDescription>Define the slot configuration for ad serving</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Templates */}
                  {!editingSlot && (
                    <div>
                      <Label className="mb-2 block">Quick Templates</Label>
                      <div className="flex flex-wrap gap-2">
                        {SLOT_TEMPLATES.map(template => (
                          <Button
                            key={template.id}
                            variant="outline"
                            size="sm"
                            onClick={() => applyTemplate(template)}
                          >
                            {template.name}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Slot ID *</Label>
                      <Input
                        value={slotForm.slot_id}
                        onChange={(e) => setSlotForm({ ...slotForm, slot_id: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                        placeholder="sponsored_listings"
                        disabled={!!editingSlot}
                      />
                      <p className="text-xs text-gray-500">Unique identifier (lowercase, underscores)</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        value={slotForm.name}
                        onChange={(e) => setSlotForm({ ...slotForm, name: e.target.value })}
                        placeholder="Sponsored Listings"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Slot Type</Label>
                      <Select 
                        value={slotForm.slot_type} 
                        onValueChange={(v) => setSlotForm({ ...slotForm, slot_type: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(SLOT_TYPES).map(([key, { label }]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Pricing Model</Label>
                      <Select 
                        value={slotForm.pricing_model} 
                        onValueChange={(v) => setSlotForm({ ...slotForm, pricing_model: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(PRICING_MODELS).map(([key, { label }]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>CPM Rate (credits)</Label>
                      <Input
                        type="number"
                        value={slotForm.cpm_rate}
                        onChange={(e) => setSlotForm({ ...slotForm, cpm_rate: parseInt(e.target.value) || 0 })}
                      />
                      <p className="text-xs text-gray-500">Cost per 1000 impressions</p>
                    </div>
                    <div className="space-y-2">
                      <Label>CPC Rate (credits)</Label>
                      <Input
                        type="number"
                        value={slotForm.cpc_rate}
                        onChange={(e) => setSlotForm({ ...slotForm, cpc_rate: parseInt(e.target.value) || 0 })}
                      />
                      <p className="text-xs text-gray-500">Cost per click</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Min Budget</Label>
                      <Input
                        type="number"
                        value={slotForm.min_budget}
                        onChange={(e) => setSlotForm({ ...slotForm, min_budget: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Max Products</Label>
                      <Input
                        type="number"
                        value={slotForm.max_products}
                        onChange={(e) => setSlotForm({ ...slotForm, max_products: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Active</p>
                      <p className="text-sm text-gray-500">Enable this slot for ad serving</p>
                    </div>
                    <Switch
                      checked={slotForm.is_active}
                      onCheckedChange={(checked) => setSlotForm({ ...slotForm, is_active: checked })}
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button variant="outline" onClick={() => { setShowSlotForm(false); setEditingSlot(null); }}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveSlot}>
                      {editingSlot ? 'Update Slot' : 'Create Slot'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick link to existing Ad Inventory Pricing page */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Full Ad Inventory Pricing Management</h3>
                    <p className="text-sm text-gray-600">Manage detailed pricing rules, targeting options, and advanced settings</p>
                  </div>
                  <Button variant="outline" onClick={() => navigate('/marketing/ad-inventory')}>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Full Manager
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Slots Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {slots.map(slot => {
                const slotType = SLOT_TYPES[slot.slot_type] || { label: slot.slot_type, color: 'bg-gray-100 text-gray-800' };
                const pricingModel = PRICING_MODELS[slot.pricing_model] || PRICING_MODELS.CPM;
                
                return (
                  <Card key={slot.slot_id} className={`hover:shadow-md transition-shadow ${!slot.is_active ? 'opacity-60' : ''}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-base">{slot.name || slot.slot_id}</CardTitle>
                          <code className="text-xs text-gray-500">{slot.slot_id}</code>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEditSlot(slot)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteSlot(slot.slot_id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        <Badge className={slotType.color}>{slotType.label}</Badge>
                        <Badge variant="outline" className={pricingModel.color}>
                          {slot.pricing_model}
                        </Badge>
                        {!slot.is_active && (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-500">CPM Rate:</span>
                          <span className="ml-1 font-medium">{slot.cpm_rate} credits</span>
                        </div>
                        <div>
                          <span className="text-gray-500">CPC Rate:</span>
                          <span className="ml-1 font-medium">{slot.cpc_rate} credits</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Min Budget:</span>
                          <span className="ml-1 font-medium">{slot.min_budget} credits</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Max Products:</span>
                          <span className="ml-1 font-medium">{slot.max_products}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              
              {slots.length === 0 && (
                <div className="col-span-full text-center py-12 bg-gray-50 rounded-lg">
                  <LayoutGrid className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="font-medium text-gray-900">No inventory slots defined</h3>
                  <p className="text-sm text-gray-500 mt-1">Create your first slot to start monetizing ad space</p>
                  <Button className="mt-4" onClick={() => { resetSlotForm(); setShowSlotForm(true); }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create First Slot
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Channels Tab */}
          <TabsContent value="channels" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Channel Configuration</h2>
              <Button onClick={() => { setShowChannelForm(true); setNewChannelCredentials(null); }}>
                <Plus className="h-4 w-4 mr-2" />
                Register Channel
              </Button>
            </div>

            {/* New channel credentials display */}
            {newChannelCredentials && (
              <Card className="border-2 border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-900">
                    <CheckCircle className="h-5 w-5" />
                    Channel Registered Successfully
                  </CardTitle>
                  <CardDescription>Save these credentials securely. The API secret will not be shown again.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Channel ID</Label>
                    <div className="flex gap-2">
                      <Input value={newChannelCredentials.channel_id} readOnly className="font-mono text-sm" />
                      <Button variant="outline" size="icon" onClick={() => copyToClipboard(newChannelCredentials.channel_id)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>API Key</Label>
                    <div className="flex gap-2">
                      <Input value={newChannelCredentials.api_key} readOnly className="font-mono text-sm" />
                      <Button variant="outline" size="icon" onClick={() => copyToClipboard(newChannelCredentials.api_key)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>API Secret</Label>
                    <div className="flex gap-2">
                      <Input value={newChannelCredentials.api_secret} readOnly className="font-mono text-sm" />
                      <Button variant="outline" size="icon" onClick={() => copyToClipboard(newChannelCredentials.api_secret)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="p-3 bg-amber-100 border border-amber-300 rounded text-amber-800 text-sm flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Store these credentials in a secure location. You will not be able to retrieve the API secret after closing this panel.</span>
                  </div>
                  <Button variant="outline" onClick={() => setNewChannelCredentials(null)}>
                    I have saved the credentials
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Channel form */}
            {showChannelForm && !newChannelCredentials && (
              <Card className="border-2 border-blue-200">
                <CardHeader>
                  <CardTitle>Register New Channel</CardTitle>
                  <CardDescription>Generate API credentials for a new channel integration</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Channel Type</Label>
                      <Select 
                        value={channelForm.channel_type} 
                        onValueChange={(v) => setChannelForm({ ...channelForm, channel_type: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="own_website">Own Website (React/Next.js)</SelectItem>
                          <SelectItem value="mobile_app">Mobile App (iOS/Android)</SelectItem>
                          <SelectItem value="shopify">Shopify Storefront</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Channel Name</Label>
                      <Input
                        value={channelForm.name}
                        onChange={(e) => setChannelForm({ ...channelForm, name: e.target.value })}
                        placeholder="e.g., iOS App, Main Website"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setShowChannelForm(false)}>Cancel</Button>
                    <Button onClick={handleRegisterChannel}>Generate API Credentials</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Channels list */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {channels.map(channel => {
                const channelType = CHANNEL_TYPES[channel.channel_type] || CHANNEL_TYPES.own_website;
                const ChannelIcon = channelType.icon;
                
                return (
                  <Card key={channel.channel_id}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${channelType.color}`}>
                          <ChannelIcon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium">{channel.name}</h3>
                          <p className="text-sm text-gray-500">{channelType.label}</p>
                          <div className="mt-2 flex items-center gap-2">
                            <Badge variant={channel.status === 'active' ? 'default' : 'secondary'}>
                              {channel.status}
                            </Badge>
                            <span className="text-xs text-gray-400">
                              ID: {channel.channel_id.slice(0, 8)}...
                            </span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {channel.permissions?.map(perm => (
                              <Badge key={perm} variant="outline" className="text-xs">
                                {perm.replace('_', ' ')}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Default Shopify channel */}
              <Card className="border-dashed">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-green-100 text-green-800">
                      <Globe className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium">Shopify Storefront</h3>
                      <p className="text-sm text-gray-500">Default Channel</p>
                      <div className="mt-2">
                        <Badge className="bg-green-500">Active</Badge>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        No API key required - uses Shopify session
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Campaigns Tab */}
          <TabsContent value="campaigns" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Active Campaigns</h2>
              <Button variant="outline" onClick={() => navigate('/admin/campaigns')}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Full Campaign Dashboard
              </Button>
            </div>

            {/* Quick link to existing Campaigns page */}
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Manage Campaigns</h3>
                    <p className="text-sm text-gray-600">Review, approve, and manage vendor advertising campaigns</p>
                  </div>
                  <Button onClick={() => navigate('/admin/campaigns')}>
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Go to Campaigns
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Campaign preview from marketing_campaigns */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {campaigns.slice(0, 6).map(campaign => (
                <Card key={campaign.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium">{campaign.name}</h3>
                        <p className="text-sm text-gray-500">{campaign.ad_type}</p>
                      </div>
                      <Badge variant={campaign.status === 'active' ? 'default' : 'secondary'}>
                        {campaign.status}
                      </Badge>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Budget:</span>
                        <span className="ml-1 font-medium">{campaign.total_budget} credits</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Spent:</span>
                        <span className="ml-1 font-medium">{campaign.spent_credits || 0} credits</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Impressions:</span>
                        <span className="ml-1 font-medium">{campaign.impressions || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Clicks:</span>
                        <span className="ml-1 font-medium">{campaign.clicks || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {campaigns.length === 0 && (
                <div className="col-span-full text-center py-12 bg-gray-50 rounded-lg">
                  <TrendingUp className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="font-medium text-gray-900">No active campaigns</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Vendors can create campaigns through their Advertising dashboard
                  </p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default VIMDashboard;
